import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Close, Download } from "@mui/icons-material";
import { APIProposal } from "../api/proposals";
import { APICustomer } from "../api/customers";
import { formatMoney } from "../utils";
import style from "./DrillDown.module.css";

// The records behind one number on the Analytics page.
export type Drill = {
  title: string;
  note?: string;
  proposals?: APIProposal[];
  customers?: APICustomer[];
  dateOf?: (p: APIProposal) => number;
  dateLabel?: string;
};

type SortKey = "date" | "customer" | "amount";

const STATUS_LABEL: Record<string, string> = {
  LEAD: "Lead",
  QUOTED: "Quoted",
  WON: "Won",
  DONE: "Done",
};

const fmtDate = (ms: number) =>
  ms ? new Date(ms).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "";

const customerName = (p: { firstName: string; lastName: string }) =>
  `${p.firstName} ${p.lastName}`.trim() || "Unnamed customer";

// "Tree removal, Stump grinding +2 more"
const workSummary = (p: APIProposal) => {
  const names = (p.lines || []).map((l) => l.productName).filter(Boolean);
  if (names.length === 0) return "";
  return names.slice(0, 2).join(", ") + (names.length > 2 ? ` +${names.length - 2} more` : "");
};

function downloadCsv(name: string, rows: (string | number)[][]) {
  const csv = rows
    .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name.replace(/[^\w -]+/g, "").trim() + ".csv";
  a.click();
  URL.revokeObjectURL(url);
}

export default function DrillDown({
  drill,
  onClose,
}: {
  drill: Drill | null;
  onClose: () => void;
}) {
  const phone = useMediaQuery("(max-width: 699px)");
  const navigate = useNavigate();
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: "date", desc: true });

  const dateOf = drill?.dateOf || ((p: APIProposal) => p.createdAt || 0);
  const proposals = useMemo(() => {
    const list = [...(drill?.proposals || [])];
    const dir = sort.desc ? -1 : 1;
    list.sort((a, b) => {
      if (sort.key === "amount") return dir * ((a.totalAmount || 0) - (b.totalAmount || 0));
      if (sort.key === "customer") return dir * customerName(a).localeCompare(customerName(b));
      return dir * (dateOf(a) - dateOf(b));
    });
    return list;
    // dateOf is derived from drill
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drill, sort]);
  const customers = useMemo(
    () => [...(drill?.customers || [])].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)),
    [drill]
  );

  if (!drill) return null;
  const isCustomers = !!drill.customers;
  const total = proposals.reduce((s, p) => s + (p.totalAmount || 0), 0);

  const sortBy = (key: SortKey) =>
    setSort((s) => ({ key, desc: s.key === key ? !s.desc : key !== "customer" }));
  const arrow = (key: SortKey) => (sort.key === key ? (sort.desc ? " ↓" : " ↑") : "");
  const openProposal = (p: APIProposal) => navigate(`/proposals?id=${p.id}`);

  const exportCsv = () =>
    isCustomers
      ? downloadCsv(drill.title, [
          ["Name", "Phone", "Email", "Address", "City", "Added"],
          ...customers.map((c) => [
            `${c.firstName} ${c.lastName}`.trim(),
            c.phone,
            c.email,
            c.addressLine1,
            c.city,
            fmtDate(c.createdAt || 0),
          ]),
        ])
      : downloadCsv(drill.title, [
          [drill.dateLabel || "Date", "Customer", "Work", "Status", "Subtotal", "Tax", "Total"],
          ...proposals.map((p) => [
            fmtDate(dateOf(p)),
            customerName(p),
            (p.lines || []).map((l) => l.productName).filter(Boolean).join("; "),
            (STATUS_LABEL[p.status] || p.status) + (p.archivedAt ? " (archived)" : ""),
            (p.subtotalAmount || 0).toFixed(2),
            (p.taxAmount || 0).toFixed(2),
            (p.totalAmount || 0).toFixed(2),
          ]),
        ]);

  const status = (p: APIProposal) => (
    <span className={style.statusCell}>
      <Chip
        size="small"
        label={STATUS_LABEL[p.status] || p.status}
        color={p.status === "WON" || p.status === "DONE" ? "success" : "default"}
        variant={p.status === "DONE" ? "outlined" : "filled"}
      />
      {p.archivedAt ? <Chip size="small" label="Archived" variant="outlined" /> : null}
    </span>
  );

  return (
    <Dialog open onClose={onClose} fullScreen={phone} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle className={style.header}>
        <div>
          <div className={style.title}>{drill.title}</div>
          <Typography variant="body2" color="text.secondary">
            {isCustomers
              ? `${customers.length} customer${customers.length === 1 ? "" : "s"}`
              : `${proposals.length} proposal${proposals.length === 1 ? "" : "s"} · ${formatMoney(total)}`}
            {drill.note ? ` · ${drill.note}` : ""}
          </Typography>
        </div>
        <div className={style.headerActions}>
          <Button
            size="small"
            startIcon={<Download />}
            onClick={exportCsv}
            disabled={isCustomers ? !customers.length : !proposals.length}
          >
            CSV
          </Button>
          <IconButton onClick={onClose} aria-label="Close">
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers className={style.content}>
        {(isCustomers ? customers.length : proposals.length) === 0 ? (
          <Typography color="text.secondary" className={style.empty}>
            Nothing here for this period.
          </Typography>
        ) : isCustomers ? (
          <table className={style.table}>
            <thead>
              <tr>
                <th>Name</th>
                {!phone && <th>City</th>}
                {!phone && <th>Phone</th>}
                <th>Added</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr
                  key={c.id}
                  className={style.row}
                  onClick={() => navigate(`/customers?id=${c.id}`)}
                >
                  <td>
                    <Link to={`/customers?id=${c.id}`} onClick={(e) => e.stopPropagation()}>
                      {`${c.firstName} ${c.lastName}`.trim() || "Unnamed customer"}
                    </Link>
                  </td>
                  {!phone && <td>{c.city}</td>}
                  {!phone && <td>{c.phone}</td>}
                  <td>{fmtDate(c.createdAt || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : phone ? (
          <div className={style.cards}>
            {proposals.map((p) => (
              <button
                key={p.id}
                type="button"
                className={style.card}
                onClick={() => openProposal(p)}
              >
                <div className={style.cardTop}>
                  <strong>{customerName(p)}</strong>
                  <strong>{formatMoney(p.totalAmount || 0)}</strong>
                </div>
                <div className={style.muted}>{workSummary(p) || "No line items"}</div>
                <div className={style.cardTop}>
                  <span className={style.muted}>
                    {drill.dateLabel || "Created"} {fmtDate(dateOf(p))}
                  </span>
                  {status(p)}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <table className={style.table}>
            <thead>
              <tr>
                <th>
                  <button type="button" onClick={() => sortBy("date")}>
                    {drill.dateLabel || "Created"}
                    {arrow("date")}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => sortBy("customer")}>
                    Customer{arrow("customer")}
                  </button>
                </th>
                <th>Work</th>
                <th>Status</th>
                <th className={style.num}>
                  <button type="button" onClick={() => sortBy("amount")}>
                    Total{arrow("amount")}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {proposals.map((p) => (
                <tr
                  key={p.id}
                  className={style.row}
                  onClick={() => openProposal(p)}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && openProposal(p)}
                  title="Open this proposal"
                >
                  <td className={style.nowrap}>{fmtDate(dateOf(p))}</td>
                  <td>
                    <Link
                      to={`/customers?id=${p.customerId}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {customerName(p)}
                    </Link>
                  </td>
                  <td className={style.muted}>{workSummary(p)}</td>
                  <td>{status(p)}</td>
                  <td className={style.num}>{formatMoney(p.totalAmount || 0)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4}>Total</td>
                <td className={style.num}>{formatMoney(total)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}
